import { useQueries, useQuery, UseQueryResult } from '@tanstack/react-query';

import { ICON_URL_STALE_TIME } from '../../app/constants';
import { queryClient } from '../../api/queryClient';
import { RoleListBody } from '../../api/generated/pap';
import {
  getV1BusinessPartnerCurrentWithCurrentIdentityInfoOptions,
  getV1BusinessPartnerCurrentWithCurrentIdentityInfoQueryKey,
  getV1IdentityCurrentOptions,
  getV1IdentityCurrentQueryKey,
  getV1RolesCurrentOptions as getPapRolesCurrentOptions,
} from '../../api/generated/pap/@tanstack/react-query.gen';
import { getV1RolesCurrentOptions as getPmRolesCurrentOptions } from '../../api/generated/pm/@tanstack/react-query.gen';
import { UserRole } from '../../types';
import { AppService } from '../../providers/app/app.types';
import { BusinessPartnerType } from '../../app/routes/utils';
import { useCurrentBusinessProfiles } from './profiles.queries';

/**
 * App-wide session hooks: the current Identity, its roles and its current
 * Business Partner. `useAppData` composes them for the app provider, so they
 * live here rather than next to a single feature.
 */

export const useCurrentIdentity = (external: boolean, enabled: boolean = true) =>
  useQuery({
    ...getV1IdentityCurrentOptions({ query: { external: external ? 'true' : 'false' } }),
    enabled,
  });

/** Clears every `external` variant of the current-identity query. */
export const invalidateCurrentIdentity = () =>
  queryClient.invalidateQueries({ queryKey: getV1IdentityCurrentQueryKey() });

const combineRoles = (results: UseQueryResult<RoleListBody>[]) => ({
  data: results.every((result) => result.data)
    ? {
        ...results[0].data,
        items: Array.from(new Set(results.flatMap((result) => result.data?.items ?? []))),
      }
    : undefined,
  isFetched: results.every((result) => result.isFetched),
});

/**
 * PAP and PM expose distinct role sets at the same path, so this issues one
 * request to each and returns the de-duplicated union of their `items`.
 */
export const useCurrentRole = () =>
  useQueries({
    queries: [getPapRolesCurrentOptions(), getPmRolesCurrentOptions()],
    combine: combineRoles,
  });

/** Icon URLs in the response are presigned and expire, hence the stale time. */
export const useCurrentBusinessPartnerWithIdentityInfo = () =>
  useQuery({
    ...getV1BusinessPartnerCurrentWithCurrentIdentityInfoOptions(),
    staleTime: ICON_URL_STALE_TIME,
    refetchOnMount: true,
  });

export const invalidateCurrentBusinessPartnerWithIdentityInfo = () =>
  queryClient.invalidateQueries({
    queryKey: getV1BusinessPartnerCurrentWithCurrentIdentityInfoQueryKey(),
  });

export function useAppData(): AppService {
  const { data: currentBusinessPartner, isFetched: loadedBusinessPartner } =
    useCurrentBusinessPartnerWithIdentityInfo();
  const { data: identity, isFetched: loadedIdentity } = useCurrentIdentity(true);
  const { data: roles, isFetched: loadedRoles } = useCurrentRole();

  const userRoles = (roles?.items || []) as UserRole[];

  const isUserAdmin = userRoles.includes(UserRole.UserAdministrator);
  const { data: bpProfiles, isFetched: loadedProfiles } = useCurrentBusinessProfiles(isUserAdmin);

  const isLoading = !loadedBusinessPartner || !loadedIdentity || !loadedProfiles || !loadedRoles;

  const availableProfiles = bpProfiles?.items ?? [];

  const isBusinessPartnerAdmin = userRoles.includes(UserRole.BusinessPartnerAdministrator);
  const isCustomerAdmin = userRoles.includes(UserRole.ServiceCustomerAdministrator);
  const isItAdmin = userRoles.includes(UserRole.ItAdministrator);
  const isOwner = currentBusinessPartner?.isOwner || false;
  const isDealer = currentBusinessPartner?.type === BusinessPartnerType.Dealer;

  let pingMap: Map<string, string>;

  try {
    pingMap = new Map<string, string>(JSON.parse(import.meta.env.VITE_PING_MAP ?? []));
  } catch {
    pingMap = new Map<string, string>();
  }
  const isIdentifiedThroughPing = identity?.externalIdentities?.find(
    (idp) => !!pingMap.get(idp?.realm ?? '')
  );

  const pingIdentityBaseUrl = pingMap.get(isIdentifiedThroughPing?.realm || '');

  return {
    currentBusinessPartner,
    identity,
    userRoles,
    loadedBusinessPartner,
    loadedIdentity,
    isOwner,
    isDealer,
    isUserAdmin,
    isCustomerAdmin,
    isBusinessPartnerAdmin,
    isItAdmin,
    availableProfiles,
    loadedProfiles,
    loadedRoles,
    isLoading,
    isIdentifiedThroughPing: !!isIdentifiedThroughPing,
    pingIdentityBaseUrl,
  } as AppService;
}
